/**
 * Turning a freehand stroke into a few smooth curves. For the pencil.
 *
 * A mouse reports a point every few milliseconds, and a hand is not steady: a
 * three-second squiggle is two hundred points with a tremor in them. Stored as
 * they are, that is two hundred anchors to drag in node editing, a press file
 * with two hundred segments, and every wobble printed faithfully.
 *
 * So the stroke is fitted with cubic curves by Philip Schneider's method
 * (Graphics Gems, 1990), the standard answer to this problem. It fits one curve
 * to the whole stroke, measures the worst miss, and splits at that point and
 * recurses until every piece is close enough. The joins are smooth because
 * both halves share the tangent at the split, which is what makes the result
 * look drawn rather than traced.
 *
 * The fit is given every point the mouse reported. Thinning them first was
 * tried, and measurably worse: the fit checks its error against the points it
 * is given, so the curve strayed from the ones thinning had thrown away.
 *
 * Pure geometry in millimetres; the tolerance is the caller's, since it depends
 * on the zoom the stroke was drawn at.
 */

import type { PathNode } from '../model/types';

interface V {
  x: number;
  y: number;
}

type Cubic = [ V, V, V, V ];

const add = ( a: V, b: V ): V => ( { x: a.x + b.x, y: a.y + b.y } );
const sub = ( a: V, b: V ): V => ( { x: a.x - b.x, y: a.y - b.y } );
const scale = ( a: V, s: number ): V => ( { x: a.x * s, y: a.y * s } );
const dot = ( a: V, b: V ) => a.x * b.x + a.y * b.y;
const length = ( a: V ) => Math.hypot( a.x, a.y );
const distance = ( a: V, b: V ) => length( sub( a, b ) );

function unit( a: V ): V {
  const l = length( a );

  return l === 0 ? { x: 0, y: 0 } : { x: a.x / l, y: a.y / l };
}

/* ---------------------------------------------------------------
 * Fitting
 * ------------------------------------------------------------ */

/** A point on a cubic. */
function bezier( c: Cubic, t: number ): V {
  const u = 1 - t;

  return add(
    add( scale( c[ 0 ], u * u * u ), scale( c[ 1 ], 3 * u * u * t ) ),
    add( scale( c[ 2 ], 3 * u * t * t ), scale( c[ 3 ], t * t * t ) )
  );
}

/** Its first and second derivatives, for Newton's method. */
function bezierD1( c: Cubic, t: number ): V {
  const u = 1 - t;

  return add(
    add( scale( sub( c[ 1 ], c[ 0 ] ), 3 * u * u ), scale( sub( c[ 2 ], c[ 1 ] ), 6 * u * t ) ),
    scale( sub( c[ 3 ], c[ 2 ] ), 3 * t * t )
  );
}

function bezierD2( c: Cubic, t: number ): V {
  return add(
    scale( add( sub( c[ 2 ], scale( c[ 1 ], 2 ) ), c[ 0 ] ), 6 * ( 1 - t ) ),
    scale( add( sub( c[ 3 ], scale( c[ 2 ], 2 ) ), c[ 1 ] ), 6 * t )
  );
}

/**
 * Cubics through a stroke, each within `tolerance` of every point it replaces.
 */
export function fitCurves( points: V[], tolerance: number ): Cubic[] {
  // Two samples on top of each other say nothing about direction, and would
  // give the fit a tangent of zero length.
  const clean = points.filter( ( p, i ) => i === 0 || distance( p, points[ i - 1 ] as V ) > 1e-6 );

  if ( clean.length < 2 ) return [];

  const first = clean[ 0 ] as V;
  const last = clean[ clean.length - 1 ] as V;

  const left = unit( sub( clean[ 1 ] as V, first ) );
  const right = unit( sub( clean[ clean.length - 2 ] as V, last ) );

  return fitCubic( clean, left, right, tolerance );
}

function fitCubic( points: V[], leftTangent: V, rightTangent: V, tolerance: number ): Cubic[] {
  const first = points[ 0 ] as V;
  const last = points[ points.length - 1 ] as V;

  // Two points: a straight curve, handles a third of the way along.
  if ( points.length === 2 ) {
    const d = distance( first, last ) / 3;

    return [ [ first, add( first, scale( leftTangent, d ) ), add( last, scale( rightTangent, d ) ), last ] ];
  }

  let u = chordLengths( points );
  let curve = generate( points, u, leftTangent, rightTangent );
  let [ worst, split ] = maxError( points, curve, u );

  if ( worst < tolerance ) return [ curve ];

  // Close: try moving each point's place along the curve before giving up and
  // splitting. A few rounds of this is usually enough.
  if ( worst < tolerance * 4 ) {
    for ( let round = 0; round < 20; round++ ) {
      const better = reparameterize( curve, points, u );

      curve = generate( points, better, leftTangent, rightTangent );
      [ worst, split ] = maxError( points, curve, better );

      if ( worst < tolerance ) return [ curve ];

      u = better;
    }
  }

  // Split at the worst point. Both halves share the tangent there, which is
  // what keeps the join smooth.
  const centre = unit( sub( points[ split - 1 ] as V, points[ split + 1 ] as V ) );
  const opposite = scale( centre, -1 );

  return fitCubic( points.slice( 0, split + 1 ), leftTangent, centre, tolerance ).concat(
    fitCubic( points.slice( split ), opposite, rightTangent, tolerance )
  );
}

/** Where each point sits along the stroke, as a share of its length. */
function chordLengths( points: V[] ): number[] {
  const u = [ 0 ];

  for ( let i = 1; i < points.length; i++ ) {
    u.push( ( u[ i - 1 ] as number ) + distance( points[ i ] as V, points[ i - 1 ] as V ) );
  }

  const total = u[ u.length - 1 ] as number;

  return u.map( ( value ) => ( total === 0 ? 0 : value / total ) );
}

/**
 * The best cubic with these end tangents, by least squares. Only the two
 * handle lengths are unknown, so it is a two-by-two system.
 */
function generate( points: V[], u: number[], leftTangent: V, rightTangent: V ): Cubic {
  const first = points[ 0 ] as V;
  const last = points[ points.length - 1 ] as V;

  let c00 = 0;
  let c01 = 0;
  let c11 = 0;
  let x0 = 0;
  let x1 = 0;

  for ( let i = 0; i < points.length; i++ ) {
    const t = u[ i ] as number;
    const s = 1 - t;
    const a0 = scale( leftTangent, 3 * s * s * t );
    const a1 = scale( rightTangent, 3 * s * t * t );

    c00 += dot( a0, a0 );
    c01 += dot( a0, a1 );
    c11 += dot( a1, a1 );

    const straight = bezier( [ first, first, last, last ], t );
    const miss = sub( points[ i ] as V, straight );

    x0 += dot( a0, miss );
    x1 += dot( a1, miss );
  }

  const det = c00 * c11 - c01 * c01;
  let alphaLeft = det === 0 ? 0 : ( x0 * c11 - x1 * c01 ) / det;
  let alphaRight = det === 0 ? 0 : ( c00 * x1 - c01 * x0 ) / det;

  // A handle of no length, or one pointing backwards, is the least-squares
  // answer to a degenerate stroke. The fallback is Wu and Barsky's: a third of
  // the distance, which is never wrong by much.
  const span = distance( first, last );
  const epsilon = 1e-6 * span;

  if ( alphaLeft < epsilon || alphaRight < epsilon ) {
    alphaLeft = span / 3;
    alphaRight = span / 3;
  }

  return [ first, add( first, scale( leftTangent, alphaLeft ) ), add( last, scale( rightTangent, alphaRight ) ), last ];
}

/** Newton-Raphson: each point's place on the curve, nudged towards the nearest. */
function reparameterize( curve: Cubic, points: V[], u: number[] ): number[] {
  return points.map( ( p, i ) => {
    const t = u[ i ] as number;
    const miss = sub( bezier( curve, t ), p );
    const d1 = bezierD1( curve, t );
    const d2 = bezierD2( curve, t );
    const denominator = dot( d1, d1 ) + dot( miss, d2 );

    if ( denominator === 0 ) return t;

    return Math.max( 0, Math.min( 1, t - dot( miss, d1 ) / denominator ) );
  } );
}

/** The worst miss, and where it was. Never at an end, which the curve passes through. */
function maxError( points: V[], curve: Cubic, u: number[] ): [ number, number ] {
  let worst = 0;
  let at = Math.floor( points.length / 2 );

  for ( let i = 1; i < points.length - 1; i++ ) {
    const d = distance( bezier( curve, u[ i ] as number ), points[ i ] as V );

    if ( d > worst ) {
      worst = d;
      at = i;
    }
  }

  return [ worst, at ];
}

/* ---------------------------------------------------------------
 * Into points a layer can store
 * ------------------------------------------------------------ */

/**
 * The fitted curves as the pen's own kind of points: an anchor at every join,
 * its handles the two control points either side. What the pen makes and what
 * the pencil makes are then the same thing, edited the same way.
 */
export function toNodes( curves: Cubic[], closed: boolean ): PathNode[] {
  const nodes: PathNode[] = [];

  curves.forEach( ( [ start, out, into, end ], index ) => {
    if ( index === 0 ) nodes.push( { x: start.x, y: start.y, out } );
    else {
      const join = nodes[ nodes.length - 1 ] as PathNode;

      nodes[ nodes.length - 1 ] = { ...join, out };
    }

    nodes.push( { x: end.x, y: end.y, in: into } );
  } );

  // Closed: the last anchor is the first one again. The two become one,
  // keeping the handle each brought.
  if ( closed && nodes.length > 2 ) {
    const last = nodes.pop() as PathNode;
    const first = nodes[ 0 ] as PathNode;

    nodes[ 0 ] = last.in ? { ...first, in: last.in } : first;
  }

  return nodes;
}
